/**
 * The « mise en service » of an ephemeral branch, as data: where each step
 * stands, the three milestones of the « Mise en service » card, when the
 * branch page shows that card (compact) after the hand-over, when it shows
 * the « environment ready » banner, the durations it says, and the banner's
 * dismissals. Pure functions over what the page already reads — the
 * pipeline, the Application —, no request of their own.
 *
 * Read by `BranchPreparing.tsx` (before the Application exists),
 * `BranchService.tsx` (the cards) and `BranchReady.tsx` (banner and report).
 */

import { deployedImages, projectSource, type ArgoApplication } from './argoApp';
import {
  isApplicationSettled,
  SETTLING_RECENT_MS,
} from './applicationSettling';
import { isEphemeralBranch } from './branchKind';
import { parseImage } from './images';
import { applicationState, isApplicationMoving } from './podsFollowUp';
import {
  isPipelineRunning,
  pipelineChipStatus,
  type PipelineInfo,
} from './pipelines';

/**
 * A step's state, in the words of the deletion page's timeline
 * (`.waadoo-deletion-step[data-state]`, styles.css), whose look the branch's
 * timeline shares.
 */
export type ServiceState = 'done' | 'running' | 'pending' | 'failed';

export type ServiceStepKey =
  | 'git'
  | 'build'
  | 'detection'
  | 'deployment'
  | 'ready';

/** One step of the timeline, and how long it took or has run, when known. */
export interface ServiceStep {
  key: ServiceStepKey;
  state: ServiceState;
  durationMs?: number;
}

/**
 * Where the environment stands: its Application awaited (`detecting`), there
 * and rolling out (`deploying`), settled healthy (`ready`).
 */
export type EnvironmentStage = 'detecting' | 'deploying' | 'ready';

/** An ISO timestamp in epoch ms; undefined when absent or unparsable. */
function time(value: string | undefined): number | undefined {
  if (!value) {
    return undefined;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * The build's state from its pipeline. None read yet is in progress: the
 * push that created the branch starts one, the forge only lists it a moment
 * later. Canceled and skipped (the chip's neutral `unknown`) are neither
 * done nor failed: pending, the pill then saying the forge's own word.
 */
export function buildState(pipeline?: PipelineInfo): ServiceState {
  if (!pipeline) {
    return 'running';
  }
  switch (pipelineChipStatus(pipeline.status)) {
    case 'healthy':
      return 'done';
    case 'progressing':
      return 'running';
    case 'failed':
    case 'degraded':
      return 'failed';
    default:
      return 'pending';
  }
}

/**
 * How long the build took — its run's start to its last update, once
 * finished — or has run so far, at `now`. Undefined without a start, or a
 * finished run without its last update; never negative.
 */
export function buildDurationMs(
  pipeline: PipelineInfo | undefined,
  now: number,
): number | undefined {
  const started = time(pipeline?.startedAt);
  if (!pipeline || started === undefined) {
    return undefined;
  }
  const end = isPipelineRunning(pipeline.status)
    ? now
    : time(pipeline.updatedAt);
  return end === undefined ? undefined : Math.max(0, end - started);
}

/**
 * Whether the Application runs the project's own Odoo image: one of the
 * images ArgoCD lists (`status.summary.images`, in the list answer too) is
 * named after the project — its repository path, host removed, is the
 * forge project's path or under it (the `repoURL` of `projectSource`,
 * argoApp.ts — `spec.source`, or the project's entry of `spec.sources`).
 * NOT by its last segment alone, unlike `rankImages` (images.ts): an empty
 * sync lists mailpit, valkey, postgresql, busybox, all valid project names.
 *
 * WHY: the project skeleton's `deploy/values-branch.yaml` ships
 * `odoo.enabled: false` (platform-catalog `scaffolder/odoo-project/
 * skeleton`), the CI's `promote` job setting it to true with the first image
 * tag. Until then the Application syncs EMPTY — Synced and Healthy with no
 * Odoo, only what lies outside the `odoo` dependency (Mailpit) —, and a
 * branch cut from `dev` before its first build inherits that. Synced and
 * Healthy alone does not say Odoo runs.
 */
export function runsProjectImage(app: ArgoApplication): boolean {
  let repoPath: string | undefined;
  try {
    const repoUrl = projectSource(app).source?.repoURL?.replace(/\.git$/, '');
    repoPath = repoUrl
      ? new URL(repoUrl).pathname.replace(/^\/+|\/+$/g, '').toLowerCase()
      : undefined;
  } catch {
    repoPath = undefined;
  }
  return deployedImages(app).some(image => {
    const { path } = parseImage(image);
    return (
      repoPath !== undefined &&
      repoPath !== '' &&
      (path === repoPath || path.startsWith(`${repoPath}/`))
    );
  });
}

/**
 * The first `status.history` entry, when it is the only one — the one
 * deployment that runs now. With more, nothing says the first one ran Odoo:
 * a branch whose first sync was empty (see {@link runsProjectImage}) got
 * Odoo only from a later one, and the history does not tell which.
 */
function onlyDeployment(app: ArgoApplication) {
  const history = app.status?.history ?? [];
  return history.length === 1 ? history[0] : undefined;
}

/**
 * How long the environment's first deployment took: `deployStartedAt` to
 * `deployedAt` of {@link onlyDeployment}. Undefined once the history holds
 * more than one entry (see there).
 */
export function firstDeploymentMs(app: ArgoApplication): number | undefined {
  const first = onlyDeployment(app);
  const started = time(first?.deployStartedAt);
  const ended = time(first?.deployedAt);
  return started === undefined || ended === undefined || ended < started
    ? undefined
    : ended - started;
}

/** The deployment step at each stage of the environment. */
const DEPLOYMENT_STATES: Record<EnvironmentStage, ServiceState> = {
  detecting: 'pending',
  deploying: 'running',
  ready: 'done',
};

/**
 * The five steps and where each stands. The branch exists — the page only
 * follows a branch that does —; the build is its pipeline's; the rest
 * follows the environment's stage: detection by ArgoCD is what the page
 * waits for while there is no Application, then the deployment, then Odoo
 * ready.
 */
export function serviceSteps(options: {
  pipeline?: PipelineInfo;
  stage: EnvironmentStage;
  now: number;
  /** The first deployment's duration, once known (`firstDeploymentMs`). */
  deploymentMs?: number;
}): ServiceStep[] {
  const { pipeline, stage, now, deploymentMs } = options;
  // A ready environment runs its image: built, whether or not its run was
  // read.
  const build: ServiceStep = {
    key: 'build',
    state: !pipeline && stage === 'ready' ? 'done' : buildState(pipeline),
  };
  const buildMs = buildDurationMs(pipeline, now);
  if (buildMs !== undefined) {
    build.durationMs = buildMs;
  }
  const deployment: ServiceStep = {
    key: 'deployment',
    state: DEPLOYMENT_STATES[stage],
  };
  if (stage === 'ready' && deploymentMs !== undefined) {
    deployment.durationMs = deploymentMs;
  }
  return [
    { key: 'git', state: 'done' },
    build,
    { key: 'detection', state: stage === 'detecting' ? 'running' : 'done' },
    deployment,
    { key: 'ready', state: stage === 'ready' ? 'done' : 'pending' },
  ];
}

/** The three milestones of the « Mise en service » card. */
export type MilestoneKey = 'branch' | 'image' | 'environment';

export interface Milestone {
  key: MilestoneKey;
  state: ServiceState;
  /** The share of its bar filled, 0 to 1. */
  share: number;
}

/** A done or failed step counts whole, a running one half. */
function stepShare(state: ServiceState): number {
  if (state === 'running') {
    return 0.5;
  }
  return state === 'pending' ? 0 : 1;
}

/**
 * The milestones from the steps: the branch is the Git step, the image the
 * build, the environment the last three — done once all are, failed if one
 * is, running if one runs, and its bar filled by the same rule as each step
 * (a running step half).
 */
export function serviceMilestones(steps: ServiceStep[]): Milestone[] {
  const byKey = (key: ServiceStepKey) =>
    steps.find(step => step.key === key) as ServiceStep;
  const environment = steps.filter(step =>
    ['detection', 'deployment', 'ready'].includes(step.key),
  );
  let environmentState: ServiceState = 'pending';
  if (environment.every(step => step.state === 'done')) {
    environmentState = 'done';
  } else if (environment.some(step => step.state === 'failed')) {
    environmentState = 'failed';
  } else if (environment.some(step => step.state !== 'pending')) {
    environmentState = 'running';
  }
  const git = byKey('git');
  const build = byKey('build');
  return [
    { key: 'branch', state: git.state, share: stepShare(git.state) },
    { key: 'image', state: build.state, share: stepShare(build.state) },
    {
      key: 'environment',
      state: environmentState,
      share:
        environment.reduce((sum, step) => sum + stepShare(step.state), 0) /
        environment.length,
    },
  ];
}

/**
 * When the environment became ready, epoch ms: the `deployedAt` of its only
 * deployment ({@link onlyDeployment}). With several, undefined — no
 * duration is said, and the banner's window counts from the creation —:
 * the first one may have run nothing (an empty sync, see
 * {@link runsProjectImage}). The end of the last operation
 * (`status.operationState.finishedAt`) for an Application without history.
 */
export function readyAt(app: ArgoApplication): number | undefined {
  const history = app.status?.history ?? [];
  if (history.length === 0) {
    return time(app.status?.operationState?.finishedAt);
  }
  return time(onlyDeployment(app)?.deployedAt);
}

/**
 * How long the environment took to be ready: from the earliest start known
 * to {@link readyAt}. The starts: the run start of the branch's LATEST
 * pipeline — the one the page reads, which on a fresh branch is normally
 * the run of the push that created it — and the Application's creation,
 * which bounds it when a later push started a newer run. Undefined when
 * either end is unknown, or the end comes before the start: no duration is
 * invented.
 */
export function readyDurationMs(
  app: ArgoApplication,
  pipeline?: PipelineInfo,
): number | undefined {
  const starts = [
    time(pipeline?.startedAt),
    time(app.metadata.creationTimestamp),
  ].filter((value): value is number => value !== undefined);
  const end = readyAt(app);
  if (starts.length === 0 || end === undefined) {
    return undefined;
  }
  const duration = end - Math.min(...starts);
  return duration >= 0 ? duration : undefined;
}

/** Whether the Application was created less than `SETTLING_RECENT_MS` ago. */
function createdRecently(app: ArgoApplication, now: number): boolean {
  const created = time(app.metadata.creationTimestamp);
  return created !== undefined && now - created < SETTLING_RECENT_MS;
}

/**
 * The healths the failure banners speak for: no « en cours » over them.
 * Not `Missing`: a freshly created Application is OutOfSync + Missing until
 * its first sync, the very start of the deployment this card follows.
 */
const NOT_FOLLOWED_HEALTHS = ['Degraded'];

/**
 * Whether the branch page shows the compact « Mise en service » card after
 * the hand-over: an ephemeral branch whose Application was created less than
 * {@link SETTLING_RECENT_MS} ago and has not settled
 * (`isApplicationSettled`) — the first deployment of a fresh environment.
 * Not while its deployment fails (`failing`: the page's sync-failure
 * banner, `syncFailure.ts`), nor with health `Degraded`: the page's own
 * banners and band say it, a card saying « Déploiement en cours » would
 * contradict them. `Missing` is followed (see NOT_FOLLOWED_HEALTHS).
 */
export function showsServiceFollowUp(
  branch: string,
  app: ArgoApplication,
  now: number,
  failing: boolean,
): boolean {
  const health = app.status?.health?.status;
  return (
    isEphemeralBranch(branch) &&
    !failing &&
    !(health !== undefined && NOT_FOLLOWED_HEALTHS.includes(health)) &&
    createdRecently(app, now) &&
    !isApplicationSettled(app, now)
  );
}

/**
 * Synced, Healthy, deployed at least once, not rolling out, and running the
 * project's Odoo image ({@link runsProjectImage}: an empty sync is Synced
 * and Healthy too).
 */
function isSettledHealthy(app: ArgoApplication): boolean {
  const state = applicationState(app);
  return (
    !isApplicationMoving(state) &&
    state.sync === 'Synced' &&
    state.health === 'Healthy' &&
    (app.status?.history?.length ?? 0) > 0 &&
    runsProjectImage(app)
  );
}

/**
 * Whether the branch page says « Environnement <branche> prêt »: an
 * ephemeral branch, its Application settled healthy, and a FRESH
 * environment — ready ({@link readyAt}, its only deployment) less than
 * {@link SETTLING_RECENT_MS} after its creation, and less than
 * {@link SETTLING_RECENT_MS} ago. Without a ready time (several
 * deployments), created less than that ago: a later push moves neither.
 * Not for a permanent branch, nor for an old ephemeral one.
 */
export function showsReadyBanner(
  branch: string,
  app: ArgoApplication,
  now: number,
): boolean {
  if (!isEphemeralBranch(branch) || !isSettledHealthy(app)) {
    return false;
  }
  const created = time(app.metadata.creationTimestamp);
  if (created === undefined) {
    return false;
  }
  const ready = readyAt(app);
  return ready === undefined
    ? now - created < SETTLING_RECENT_MS
    : ready - created < SETTLING_RECENT_MS && now - ready < SETTLING_RECENT_MS;
}

/**
 * When {@link showsReadyBanner} stops holding by the clock alone, epoch ms:
 * {@link SETTLING_RECENT_MS} after the ready time, or after the creation
 * without one. Undefined without either (the banner is then not shown).
 */
export function readyBannerExpiresAt(app: ArgoApplication): number | undefined {
  const start = readyAt(app) ?? time(app.metadata.creationTimestamp);
  return start === undefined ? undefined : start + SETTLING_RECENT_MS;
}

/**
 * The namespace the new branch's environment will have, when the project's
 * environments show the pattern — as `previewAppUrl` does for the address
 * (`branchManagement.ts`). The project's ApplicationSet deploys every
 * ephemeral branch in `prj-<project>-<branch>` (`applicationset-ephemeral.yaml`
 * of the appenv skeleton), and the permanent `dev` in `prj-<project>-dev`
 * (its AppProject's destination): an environment whose namespace follows that
 * shape for its own branch confirms it, and nothing is guessed — no such
 * environment, no namespace.
 */
export function previewNamespace(
  environments: ReadonlyArray<{ branch: string; namespace?: string }>,
  project: string,
  branch: string,
): string | undefined {
  return environments.some(
    env => env.namespace === `prj-${project}-${env.branch}`,
  )
    ? `prj-${project}-${branch}`
    : undefined;
}

/**
 * The banner's dismissals, by Application uid: when each was dismissed.
 * One key for all, pruned on every write ({@link pruneDismissals}).
 */
export const READY_DISMISSED_KEY = 'waadoo.branchReady.dismissed';
/**
 * How long a dismissal is kept: the banner is shown at most
 * `SETTLING_RECENT_MS` after the first deployment, itself at most
 * `SETTLING_RECENT_MS` after the creation — a day is far past it.
 */
export const READY_DISMISSED_KEEP_MS = 24 * 60 * 60_000;
/** At most this many dismissals kept, the newest. */
export const READY_DISMISSED_MAX = 50;

type Dismissals = Record<string, number>;

/** The dismissals younger than the keep delay, the newest {@link READY_DISMISSED_MAX}. */
export function pruneDismissals(all: Dismissals, now: number): Dismissals {
  return Object.fromEntries(
    Object.entries(all)
      .filter(([, at]) => now - at < READY_DISMISSED_KEEP_MS)
      .sort(([, a], [, b]) => b - a)
      .slice(0, READY_DISMISSED_MAX),
  );
}

function readDismissals(): Dismissals {
  try {
    const parsed: unknown = JSON.parse(
      window.localStorage.getItem(READY_DISMISSED_KEY) ?? '{}',
    );
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === 'number' && Number.isFinite(entry[1]),
      ),
    );
  } catch {
    // Unreadable storage, a value that is not JSON: nothing dismissed.
    return {};
  }
}

/** Whether the banner of the Application `uid` was dismissed. */
export function isReadyBannerDismissed(uid: string, now: number): boolean {
  return uid in pruneDismissals(readDismissals(), now);
}

/** Remembers that the banner of the Application `uid` was dismissed. */
export function dismissReadyBanner(uid: string, now: number): void {
  try {
    window.localStorage.setItem(
      READY_DISMISSED_KEY,
      JSON.stringify(pruneDismissals({ ...readDismissals(), [uid]: now }, now)),
    );
  } catch {
    // Private mode, quota: the banner is closed for this page only.
  }
}
